import { getState } from './store.js';
import { t } from './i18n.js';

/**
 * Resolve the viewer's role from the store. Exported because the registry, the
 * shell and the hub page all need it and previously each carried their own
 * copy of this exact lookup (`layout.js`, `pages/catalog.js`) — one seam, one
 * implementation.
 *
 * @returns {string|null}
 */
export function currentRole() {
  const state = getState();
  if (state.user && state.user.role) return state.user.role;
  if (state.household && state.household.current_user && state.household.current_user.role) {
    return state.household.current_user.role;
  }
  return null;
}

/**
 * Static role → capability map. This is the ONE place a role's powers are
 * declared. Today both capabilities resolve to `admin`; a future
 * `product_manager` role (see `openspec/changes/household-roles-product-manager`)
 * is a single entry here rather than a sweep across the ~26 `=== 'admin'`
 * comparisons in `web/src/js`.
 *
 * @type {Record<string, ReadonlyArray<string>>}
 */
const ROLE_CAPABILITIES = {
  admin: ['manage_catalog', 'manage_household'],
};

/**
 * Whether a role holds a capability.
 *
 * A `null`/`undefined` capability means "no capability required" and is always
 * granted — that is how registry rows visible to every household member are
 * expressed (`requiredCapability: null`).
 *
 * `role` is optional so callers in the shell can stay terse. Omitting it reads
 * the current viewer's role from the store; passing it explicitly (including
 * `null`) uses that value verbatim, which is what makes the registry helpers
 * testable without a store.
 *
 * @param {string|null|undefined} capability
 * @param {string|null} [role]
 * @returns {boolean}
 */
export function hasCapability(capability, role) {
  if (capability == null) return true;
  const effective = role === undefined ? currentRole() : role;
  if (!effective) return false;
  const granted = ROLE_CAPABILITIES[effective];
  return Array.isArray(granted) && granted.includes(capability);
}

/**
 * Whether the current user may search, view and manage product masters
 * (search, inline-create, edit). Expressed through `hasCapability` rather than
 * a role literal so the product-manager role is a map change, not a code sweep.
 */
export function canManageProducts() {
  return hasCapability('manage_catalog');
}

/**
 * The sentence shown when an act is refused for lack of a capability.
 *
 * There are exactly two, keyed by CAPABILITY rather than by screen (design D1
 * of `copy-correctness-and-vocabulary`). Thirteen screens each used to carry
 * their own wording, and every one of them named a role — "Admin" — which
 * stops being true the moment a second role holds the capability. Keying by
 * capability is what makes a future `product_manager` role a map change here
 * instead of a sweep through thirteen strings.
 *
 * `subjectKey` is an existing `*.title` key. The sentence needs the nominative
 * plural, which is exactly what those keys already hold — no declined variants,
 * no new synonyms.
 *
 * @param {'manage_catalog'|'manage_household'} capability
 * @param {string} subjectKey
 * @returns {string}
 */
export function needsCapabilityText(capability, subjectKey) {
  const key = capability === 'manage_household'
    ? 'errors.needs_manage_household'
    : 'errors.needs_manage_catalog';
  return t(key, { subject: t(subjectKey) });
}
